// -----------------------------------------------------------------------------
// VoiceContext.jsx
// Global state manager for the Harbour Nest voice system.
// 
// This file centralizes all logic related to:
//  - Microphone control (start/stop listening)
//  - Speech-to-Text handling using the browser's Web Speech API
//  - Transcript storage and lifecycle
//  - Voice assistant status states (idle, listening, processing, error)
//  - Dispatching transcripts to backend or local intent processing
//  - Optional text-based (typed) queries as a secondary input path
//
// The VoiceProvider wraps any page that needs voice-first interaction.
// Pages access this functionality through the useVoiceAssistant() hook.
//
// This design keeps the voice subsystem isolated, predictable, and scalable
// as we layer in:
//  - Intent classification
//  - Context-aware responses
//  - Property-specific actions (highlight, scroll, open modals)
//  - Server-side speech processing or Whisper integration
// -----------------------------------------------------------------------------

import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
  } from "react";
  import { useAuth } from "../../context/AuthContext";

  const VoiceContext = createContext(null);
  
  /*
  VOICE STATES:
  - idle        : waiting for user
  - listening   : mic open, capturing audio
  - processing  : transcript captured, sending to backend
  - error       : any failure (mic, permissions, STT, backend)
  */
  
  export function VoiceProvider({ children }) {
    const { authFetch } = useAuth();
    const [status, setStatus] = useState("idle");
    const [transcript, setTranscript] = useState("");
    const [lastResponse, setLastResponse] = useState(null);
    const [error, setError] = useState(null);
    const [conversationHistory, setConversationHistory] = useState([]);

    const recognitionRef = useRef(null);
    const isSupportedRef = useRef(true);
  
    // ------------------------------------------------------------
    // PROCESS TRANSCRIPT (send to backend)
    // ------------------------------------------------------------
    const handleProcess = useCallback(async (text) => {
      try {
        setStatus("processing");

        // Add user message to conversation history
        const userMessage = {
          role: 'user',
          content: text,
          timestamp: new Date().toISOString(),
        };

        setConversationHistory(prev => [...prev, userMessage]);

        // Call backend API to process the voice command with conversation context
        const response = await authFetch('/api/voice/process', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            text: text,
            conversationHistory: [...conversationHistory, userMessage], // Include current message
            propertyId: null, // TODO: pass current property ID if available
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to process voice command');
        }

        const data = await response.json();

        // Add assistant response to conversation history
        const assistantMessage = {
          role: 'assistant',
          content: data.message,
          timestamp: new Date().toISOString(),
        };

        setConversationHistory(prev => [...prev, assistantMessage]);
        setLastResponse(data);
        setStatus("idle");
      } catch (err) {
        console.error("Process error:", err);
        setStatus("error");
        setError("Sorry, I couldn't process that request. Please try again.");
      }
    }, [conversationHistory, authFetch]);

    // ------------------------------------------------------------
    // INIT SPEECH RECOGNITION (browser)
    // ------------------------------------------------------------
    useEffect(() => {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;

      if (!SpeechRecognition) {
        console.warn("Speech recognition API not available.");
        isSupportedRef.current = false;
        setError("Voice recognition not supported on this device.");
        setStatus("error");
        return;
      }

      const recognition = new SpeechRecognition();

      // Recommended defaults:
      recognition.lang = "en-US"; // Changed to US for better compatibility
      recognition.interimResults = true;
      recognition.continuous = false;
      recognition.maxAlternatives = 1;

      // Use refs to track latest values in event handlers
      const latestTranscript = { current: "" };
      const shouldProcess = { current: false };

      // ------------------------------------------------------------
      // EVENT HANDLERS
      // ------------------------------------------------------------
      recognition.onstart = () => {
        console.log("🎤 Speech recognition started");
        setStatus("listening");
        setError(null);
        setTranscript("");
        latestTranscript.current = "";
        shouldProcess.current = false;
      };

      recognition.onerror = (event) => {
        console.error("🚨 Speech error:", event.error);
        setStatus("error");

        // More helpful error messages
        const errorMessages = {
          'no-speech': 'No speech detected. Please try again.',
          'audio-capture': 'Microphone not available. Check your device settings.',
          'not-allowed': 'Microphone permission denied. Click the microphone icon in your browser\'s address bar and select "Allow".',
          'network': 'Network error occurred. Check your internet connection.'
        };

        setError(errorMessages[event.error] || `Microphone error: ${event.error}`);
      };

      recognition.onresult = (event) => {
        console.log("📝 Speech result received:", event.results);

        let interimTranscript = "";
        let finalTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        const fullText = finalTranscript || interimTranscript;
        console.log("💬 Transcript:", fullText);

        latestTranscript.current = fullText;
        setTranscript(fullText);

        // Mark for processing if we have final results
        if (finalTranscript) {
          shouldProcess.current = true;
        }
      };

      recognition.onend = () => {
        console.log("🛑 Speech recognition ended. Transcript:", latestTranscript.current);

        // Process if we have a transcript
        if (latestTranscript.current.trim().length > 0 && shouldProcess.current) {
          console.log("✅ Processing transcript:", latestTranscript.current);
          handleProcess(latestTranscript.current.trim());
        } else {
          console.log("⏭️ No transcript to process, returning to idle");
          setStatus("idle");
        }
      };

      recognitionRef.current = recognition;

      return () => {
        if (recognition) {
          try {
            recognition.stop();
          } catch (e) {
            // Ignore errors on cleanup
          }
        }
        recognitionRef.current = null;
      };
    }, [handleProcess]); // Remove status and transcript from dependencies
  
    // ------------------------------------------------------------
    // START / STOP LISTENING
    // ------------------------------------------------------------
    const startListening = useCallback(async () => {
      if (!isSupportedRef.current) {
        setError("Speech recognition is not supported in this browser.");
        return;
      }

      if (!recognitionRef.current) {
        console.error("Recognition object not initialized");
        setError("Voice recognition not ready. Please refresh the page.");
        return;
      }

      try {
        setTranscript("");
        setLastResponse(null);
        setError(null);

        console.log("🎤 Requesting microphone permission...");

        // Explicitly request microphone permission first
        await navigator.mediaDevices.getUserMedia({ audio: true });

        console.log("✅ Microphone permission granted");
        setStatus("listening");

        console.log("🎤 Starting speech recognition...");
        recognitionRef.current.start();
        console.log("✅ Speech recognition started successfully");
      } catch (err) {
        console.error("❌ Error:", err);
        console.error("Error name:", err.name);
        console.error("Error message:", err.message);
        setStatus("error");

        // More specific error based on the actual error
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setError("Microphone access denied. Please click 'Allow' when your browser asks for microphone permission.");
        } else if (err.name === 'NotFoundError') {
          setError("No microphone found. Please connect a microphone and try again.");
        } else if (err.message && err.message.includes('already started')) {
          setError("Voice recognition is already running. Please refresh the page.");
        } else {
          setError(`Unable to access microphone: ${err.message}`);
        }
      }
    }, []);
  
    const stopListening = useCallback(() => {
      if (status === "listening" && recognitionRef.current) {
        setStatus("processing");
        recognitionRef.current.stop();
      }
    }, [status]);

    // ------------------------------------------------------------
    // TYPING HANDLER (secondary input path)
    // ------------------------------------------------------------
    const sendTextQuery = useCallback(
      async (text) => {
        if (!text.trim()) return;
        setTranscript(text.trim());
        await handleProcess(text.trim());
      },
      [handleProcess]
    );
  
    // ------------------------------------------------------------
    // RESET ALL STATE
    // ------------------------------------------------------------
    const reset = useCallback(() => {
      setStatus("idle");
      setTranscript("");
      setLastResponse(null);
      setError(null);
    }, []);

    // ------------------------------------------------------------
    // CLEAR CONVERSATION HISTORY
    // ------------------------------------------------------------
    const clearConversation = useCallback(() => {
      setConversationHistory([]);
      setTranscript("");
      setLastResponse(null);
      setError(null);
      setStatus("idle");
    }, []);

    // ------------------------------------------------------------
    // EXPORTABLE CONTEXT VALUE
    // ------------------------------------------------------------
    const value = {
      status,
      transcript,
      lastResponse,
      error,
      conversationHistory,
      startListening,
      stopListening,
      sendTextQuery,
      reset,
      clearConversation,
    };
  
    return (
      <VoiceContext.Provider value={value}>
        {children}
      </VoiceContext.Provider>
    );
  }
  
  // ------------------------------------------------------------
  // HOOK
  // ------------------------------------------------------------
  export function useVoiceAssistant() {
    const context = useContext(VoiceContext);
    if (!context) {
      throw new Error("useVoiceAssistant must be used within a VoiceProvider");
    }
    return context;
  }
  